import { useCallback, useEffect } from 'react';

import { useUrlSearchState } from './useUrlSearchState';
import { useAppContext } from './useAppContext';
import { useSearchStore, useSearchStoreContext } from './stores/search/useSearchStore';
import { MapMode } from './stores/search/slices/mapInteractionSlice';
import { type SearchState } from './stores/search/SearchStoreProvider';
import { debouncedUpdateUrl } from '../utils/searchProviderUtils';
import { mapSearchStateToSearchListingsFilters } from '../utils/mapFilterFields';

/**
 * Hook that handles search store subscription for URL updates and filter change events.
 *
 * @note This hook should only be used inside the SearchEffectsProvider component.
 */
export const useFiltersChangeEffect = (): void => {
  const storeContext = useSearchStoreContext();
  const {
    countryCode,
    options: { language, currency, measurementSystem },
    events,
    willReflectInitialFiltersInUrl,
    willSyncStateWithUrl,
  } = useAppContext();
  const getStateSnapshot = useSearchStore((state) => state.getStateSnapshot);
  const { isSetStateFromUrlDone, isReflectToUrlDone, setIsReflectToUrlDone } = useUrlSearchState();

  const updateUrl = useCallback(
    (state: SearchState) => {
      const filters = mapSearchStateToSearchListingsFilters(state);

      // sorting options are extended on the ui-state, so we need to remove them from the filters
      delete filters.sortingOptions;

      debouncedUpdateUrl({
        filters,
        options: {
          language,
          currency,
          measurementSystem,
          page: state.page,
        },
        countryCode,
        placeName: state.placeName,
        searchMode: state.searchMode,
        mapMode: state.mapMode,
        onUrlUpdate: events?.onUrlUpdate,
        willSyncStateWithUrl,
        hasSessionBounds: true,
        sortingOptionId: state.sortingOptionId,
      });

      return filters;
    },
    [language, currency, measurementSystem, countryCode, events?.onUrlUpdate, willSyncStateWithUrl],
  );

  useEffect(() => {
    const unsubscribe = storeContext.subscribe((searchStore) => {
      if (!isSetStateFromUrlDone) return;
      const snapshot = searchStore.getStateSnapshot();
      if (snapshot.mapMode === MapMode.MULTI_POLYGON_MODE_DRAW || snapshot.mapMode === MapMode.MULTI_POLYGON_MODE_EDIT) {
        return;
      }

      const filters = updateUrl(snapshot);

      if (events?.onFiltersChange) {
        events.onFiltersChange(filters);
      }
    });

    if (willReflectInitialFiltersInUrl && isSetStateFromUrlDone && !isReflectToUrlDone) {
      const state = getStateSnapshot();
      updateUrl(state);
      setIsReflectToUrlDone(true);
    }

    return () => {
      unsubscribe();
      // we may need to cancel the debounced update url here, but it's preventing the initial url update from happening
    };
  }, [
    updateUrl,
    willReflectInitialFiltersInUrl,
    events?.onFiltersChange,
    storeContext,
    isSetStateFromUrlDone,
    events,
    getStateSnapshot,
    isReflectToUrlDone,
    setIsReflectToUrlDone,
  ]);
};
